import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Upload } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { format } from "date-fns";
import { getBlogBySlug } from "../../../api/blog";
import LoadingSpinner from "src/components/common/LoadingSpinner";
import NotFound from "../NotFound";
import { FaFacebook, FaInstagram, FaLinkedin } from "react-icons/fa";
import BlogCard from "./BlogCard";
import { useToast } from "../../../context/ToastContext";

export default function BlogDetail() {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toc, setToc] = useState([]);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState("introduction");
  const [scrollProgress, setScrollProgress] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const contentRef = React.useRef(null);
  const scrollContainerRef = React.useRef(null);
  const { showSuccess } = useToast();

  const handleCopyLink = (e) => {
    if (e) e.preventDefault();
    navigator.clipboard.writeText(window.location.href);
    showSuccess("blog link copied");
  };

  const checkScrollability = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } =
        scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(Math.ceil(scrollLeft + clientWidth) < scrollWidth);
    }
  };

  useEffect(() => {
    checkScrollability();
    const timer = setTimeout(checkScrollability, 100); // allow layout to compute
    window.addEventListener("resize", checkScrollability);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", checkScrollability);
    };
  }, [blog]);

  useEffect(() => {
    const fetchBlog = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getBlogBySlug(slug);
        if (response.success) {
          setBlog(response.blog);
        } else {
          setError(response.error || "Failed to load blog post");
        }
      } catch (err) {
        setError(err.message || "Failed to load blog post");
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchBlog();
    }
  }, [slug]);

  useEffect(() => {
    if (blog && blog.content) {
      const headings = [
        {
          label: "Introduction",
          anchor: "introduction",
        },
      ];
      const regex = /^##\s+(.*)$/gm;
      let match;
      while ((match = regex.exec(blog.content)) !== null) {
        headings.push({
          label: match[1],
          anchor: match[1]
            .toLowerCase()
            .replace(/[^\w\s-]/g, "")
            .replace(/\s+/g, "-"),
        });
      }
      setToc(headings);
    }
  }, [blog]);

  useEffect(() => {
    const handleScroll = () => {
      if (!contentRef.current) return;
      const rect = contentRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      const offset = windowHeight / 3;
      let progress = 0;

      const currentScroll = offset - rect.top;

      if (currentScroll < 0) {
        progress = 0;
      } else if (currentScroll > rect.height) {
        progress = 100;
      } else {
        progress = (currentScroll / rect.height) * 100;
      }
      setScrollProgress(progress);

      const headings = Array.from(
        document.querySelectorAll(".blog-content-markdown h2"),
      );
      let currentActive = "introduction";

      for (let i = 0; i < headings.length; i++) {
        const h2 = headings[i];
        const h2Rect = h2.getBoundingClientRect();
        if (h2Rect.top <= offset + 50) {
          currentActive = h2.id;
        } else {
          break;
        }
      }
      setActiveSection(currentActive);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (e, id) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      const y = element.getBoundingClientRect().top + window.pageYOffset - 100;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  const scrollRelatedBlogsRight = () => {
    if (scrollContainerRef.current) {
      const cardWidth =
        scrollContainerRef.current.firstElementChild?.offsetWidth || 340;
      scrollContainerRef.current.scrollBy({
        left: cardWidth + 24,
        behavior: "smooth",
      });
    }
  };

  const scrollRelatedBlogsLeft = () => {
    if (scrollContainerRef.current) {
      const cardWidth =
        scrollContainerRef.current.firstElementChild?.offsetWidth || 340;
      scrollContainerRef.current.scrollBy({
        left: -(cardWidth + 24),
        behavior: "smooth",
      });
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error || !blog) {
    return <NotFound />;
  }

  const formattedDate = blog?.publishedAt
    ? format(new Date(blog.publishedAt), "dd MMMM yyyy")
    : blog?.createdAt
      ? format(new Date(blog.createdAt), "dd MMMM yyyy")
      : "16 March 2026";

  return (
    <div
      className="bg-white min-h-screen text-gray-900 pb-20 mt-20"
      style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
    >
      {/* Container */}
      <div className="max-w-6xl mx-auto px-6 lg:px-20 md:pt-10">
        {/* Breadcrumbs */}
        <div className="text-gray-500 text-[15px] font-medium mb-6 md:mb-10">
          <Link to="/" className="hover:text-gray-900 transition-colors">
            Home
          </Link>
          <span className="mx-2">&gt;</span>
          <Link
            to="/explore"
            className="hover:text-gray-900 transition-colors"
          >
            Blogs
          </Link>
          <span className="mx-2">&gt;</span>
          <span className="text-gray-900 truncate max-w-[200px] md:max-w-none inline-block align-bottom">
            {blog.title}
          </span>
        </div>

        {/* Hero Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center lg:mb-16">
          <div>
            <div className="flex items-center gap-4 mb-8">
              <span className="text-[#FF552E] border border-[#FF552E] rounded-[12px] px-5 py-1.5 text-sm font-medium">
                {blog.category}
              </span>
              <span className="text-gray-900 font-medium">{formattedDate}</span>
            </div>

            <h1
              id="introduction"
              className="text-[30px] md:text-[40px] font-bold leading-[1.15] mb-6 tracking-tight"
            >
              {blog.title}
            </h1>

            <p className="text-gray-900 font-medium text-[16px] mb-6">
              By {blog.author}
            </p>

            <p className="text-gray-900 text-[16px] md:text-[18px] font-bold leading-relaxed">
              {blog.intro}
            </p>
          </div>

          <div className="rounded-lg overflow-hidden aspect-[4/3] lg:aspect-square">
            <img
              src={blog.mainImage}
              alt={blog.mainImageAlt || blog.title}
              className="w-full h-full object-cover"
            />
          </div>

          <div className="lg:hidden mt-2 mb-8">
            <p className="text-gray-500 text-sm mb-3">Share this post</p>
            <div className="flex items-center gap-5 text-gray-900">
              <a
                href="https://instagram.com/gopratle"
                target="_blank"
                rel="noreferrer"
                className="hover:text-[#FF552E] transition-colors"
              >
                <FaInstagram size={22} strokeWidth={1.5} />
              </a>
              <a
                href="https://linkedin.com/company/gopratle"
                target="_blank"
                rel="noreferrer"
                className="hover:text-[#FF552E] transition-colors"
              >
                <FaLinkedin size={22} strokeWidth={1.5} />
              </a>
              <a
                href="https://x.com/gopratle"
                target="_blank"
                rel="noreferrer"
                className="hover:text-[#FF552E] transition-colors flex items-center justify-center w-[22px] h-[22px]"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M4 4l11.733 16h4.267l-11.733 -16z"></path>
                  <path d="M4 20l6.768 -6.768m2.46 -2.46l6.772 -6.772"></path>
                </svg>
              </a>
              <a
                href="https://facebook.com/profile.php?id=61577127366798"
                target="_blank"
                rel="noreferrer"
                className="hover:text-[#FF552E] transition-colors"
              >
                <FaFacebook size={22} strokeWidth={1.5} />
              </a>
              <button
                onClick={handleCopyLink}
                className="hover:text-[#FF552E] transition-colors"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2 -2v-8"></path>
                  <polyline points="16 6 12 2 8 6"></polyline>
                  <line x1="12" y1="2" x2="12" y2="15"></line>
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Content & Sidebar */}
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-20 relative mb-8 lg:mb-16">
          {/* Main Content */}
          <div className="w-full lg:w-[65%]">
            <style>{`
              .blog-content-markdown h2 {
                counter-reset: h3-counter;
              }
              .custom-h3 {
                counter-increment: h3-counter;
              }
              .custom-h3 .bullet-container::after {
                content: counter(h3-counter);
              }
              .list-decimal-custom .custom-li {
                counter-increment: my-counter;
              }
              .list-decimal-custom .custom-li .bullet-container::after {
                content: counter(my-counter);
              }
              .list-disc-custom .custom-li .bullet-container::after {
                content: "•";
              }
              /* Reset the p margin inside the custom li */
              .custom-li > div > p {
                margin: 0;
              }
            `}</style>
            <div
              ref={contentRef}
              className="prose max-w-none text-gray-700 text-[16px] leading-relaxed relative blog-content-markdown pl-6 md:pl-0"
            >
              {/* Vertical Orange Line full height with progress */}
              <div className="absolute left-0 md:-left-8 top-0 bottom-0 w-[2px] bg-gray-100">
                <div
                  className="w-full bg-[#FF552E] transition-all duration-300 ease-in-out"
                  style={{ height: `${scrollProgress}%` }}
                ></div>
              </div>

              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  h2: ({ node, children, ...props }) => {
                    const text = React.Children.toArray(children).join("");
                    const id = text
                      .toLowerCase()
                      .replace(/[^\w\s-]/g, "")
                      .replace(/\s+/g, "-");
                    return (
                      <h2
                        id={id}
                        className="text-[22px] font-bold text-gray-900 my-2"
                        {...props}
                      >
                        {children}
                      </h2>
                    );
                  },
                  h3: ({ node, children, ...props }) => (
                    <div className="flex gap-4 items-start custom-h3 my-2">
                      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-[#FFE5E0] text-[#FF552E] flex items-center justify-center font-bold text-[14px] bullet-container"></div>
                      <h3
                        className="mt-0.5 text-[16px] font-bold text-gray-900 m-0 leading-relaxed"
                        {...props}
                      >
                        {children}
                      </h3>
                    </div>
                  ),
                  p: ({ node, ...props }) => (
                    <p
                      className="mb-4 text-[15px] leading-relaxed"
                      {...props}
                    />
                  ),
                  img: ({ node, alt, ...props }) => (
                    <img
                      alt={alt || "Blog content image"}
                      className="w-full rounded-[20px] object-cover my-4 aspect-[4/3] lg:aspect-[16/9]"
                      {...props}
                    />
                  ),
                  ol: ({ node, ...props }) => (
                    <div
                      className="space-y-5 mb-10 list-decimal-custom"
                      style={{ counterReset: "my-counter" }}
                    >
                      {props.children}
                    </div>
                  ),
                  ul: ({ node, ...props }) => (
                    <div className="space-y-5 mb-10 list-disc-custom">
                      {props.children}
                    </div>
                  ),
                  li: ({ node, ...props }) => (
                    <div className="flex gap-4 items-start custom-li">
                      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-[#FFE5E0] text-[#FF552E] flex items-center justify-center font-bold text-[14px] bullet-container"></div>
                      <div className="mt-0.5 text-[16px]">{props.children}</div>
                    </div>
                  ),
                  blockquote: ({ node, ...props }) => (
                    <blockquote
                      className="border-l-4 border-[#FF552E] pl-6 py-2 my-8 italic text-[18px] text-gray-800 font-medium bg-[#FFF9F8]"
                      {...props}
                    />
                  ),
                  hr: ({ node, ...props }) => null,
                }}
              >
                {blog.content}
              </ReactMarkdown>
            </div>
          </div>

          {/* Sidebar */}
          <div className="hidden lg:block lg:w-[35%] relative lg:pl-10">
            <div className="sticky top-[120px] max-h-[calc(100vh-140px)] overflow-y-auto pb-4 scrollbar-hide">
              {toc && toc.length > 0 && (
                <ul className="space-y-3 text-[15px] mb-12">
                  {toc.map((item, idx) => (
                    <li
                      key={idx}
                      className={
                        activeSection === item.anchor
                          ? "text-gray-900 font-bold"
                          : "text-gray-500 hover:text-gray-900 cursor-pointer transition-colors"
                      }
                    >
                      <a
                        href={`#${item.anchor}`}
                        onClick={(e) => scrollToSection(e, item.anchor)}
                        className="block py-1"
                      >
                        - {item.label}
                      </a>
                    </li>
                  ))}
                </ul>
              )}

              <div className="flex items-center gap-5 text-gray-900 pt-6 border-t border-gray-100">
                <a
                  href="https://instagram.com/gopratle"
                  target="_blank"
                  rel="noreferrer"
                  className="hover:text-[#FF552E] transition-colors"
                >
                  <FaInstagram size={22} strokeWidth={1.5} />
                </a>
                <a
                  href="https://linkedin.com/company/gopratle"
                  target="_blank"
                  rel="noreferrer"
                  className="hover:text-[#FF552E] transition-colors"
                >
                  <FaLinkedin size={22} strokeWidth={1.5} />
                </a>
                <a
                  href="https://x.com/gopratle"
                  target="_blank"
                  rel="noreferrer"
                  className="hover:text-[#FF552E] transition-colors flex items-center justify-center w-[22px] h-[22px]"
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M4 4l11.733 16h4.267l-11.733 -16z"></path>
                    <path d="M4 20l6.768 -6.768m2.46 -2.46l6.772 -6.772"></path>
                  </svg>
                </a>
                <a
                  href="https://facebook.com/profile.php?id=61577127366798"
                  target="_blank"
                  rel="noreferrer"
                  className="hover:text-[#FF552E] transition-colors"
                >
                  <FaFacebook size={22} strokeWidth={1.5} />
                </a>
                <button
                  onClick={handleCopyLink}
                  className="hover:text-[#FF552E] transition-colors"
                >
                  <Upload size={22} strokeWidth={1.5} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Related Content */}
        {blog.related && blog.related.length > 0 && (
          <div className="mb-8 lg:mb-16">
            <h2 className="text-[24px] md:text-[28px] font-bold text-gray-900 mb-8">
              You may also like
            </h2>

            <div className="flex items-stretch relative">
              {/* Left Arrow Button */}
              {canScrollLeft && (
                <div className="absolute -left-10 md:-left-12 lg:-left-14 top-1/2 -translate-y-1/2 flex items-center z-10 hidden md:flex">
                  <button
                    onClick={scrollRelatedBlogsLeft}
                    className="text-[#FF552E] hover:-translate-x-1 transition-transform p-2 cursor-pointer"
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="19" y1="12" x2="5" y2="12"></line>
                      <polyline points="12 19 5 12 12 5"></polyline>
                    </svg>
                  </button>
                </div>
              )}

              <div
                ref={scrollContainerRef}
                onScroll={checkScrollability}
                className="flex items-stretch gap-6 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide w-full"
                style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
              >
                {blog.related.map((post, idx) => (
                  <BlogCard key={idx} post={post} />
                ))}
              </div>

              {/* Right Arrow Button */}
              {canScrollRight && (
                <div className="absolute -right-10 md:-right-12 lg:-right-14 top-1/2 -translate-y-1/2 flex items-center z-10 hidden md:flex">
                  <button
                    onClick={scrollRelatedBlogsRight}
                    className="text-[#FF552E] hover:translate-x-1 transition-transform p-2 cursor-pointer"
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                      <polyline points="12 5 19 12 12 19"></polyline>
                    </svg>
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* CTA Banner */}
        <div className="bg-[#303030] rounded-[24px] py-8 lg:py-16 px-6 md:px-16 text-center shadow-lg">
          <h2 className="text-[22px] md:text-[40px] font-bold text-white mb-3 md:mb-4 tracking-tight leading-snug">
            Ready to plan your perfect event?
          </h2>
          <p className="text-[#D0D0D0] text-[10px]  md:text-[18px] mb-6 md:mb-8 max-w-2xl mx-auto font-normal leading-relaxed">
            Connect with top planners, performers, and crew — all in one place.
            Stress-free and tailored for you.
          </p>
          <button className="bg-[#FF552E] text-white font-medium text-[14px] md:text-[16px] px-6 py-2.5 md:px-8 md:py-3 rounded-[8px] hover:bg-[#E64A25] transition-colors">
            Post my Event
          </button>
        </div>
      </div>
    </div>
  );
}
